<script setup lang="ts">
/**
 * Why Premium, in the template's content-block layout: a framed product
 * screenshot on one side, the headline and the four highlights with round
 * green icon tiles on the other, one button underneath.
 *
 * The old free-vs-premium feature lists are gone from here: the plan cards
 * and the comparison table on the same page already carry that information.
 */
const { t } = useI18n()
const { isAuthenticated } = useAuthUser()
const { jobPackPrice, yearlyPrice } = usePremiumPlans()

const highlights = [
  { id: 'timeSaving', icon: 'bi-stopwatch' },
  { id: 'quality', icon: 'bi-patch-check' },
  { id: 'advantage', icon: 'bi-lightning-charge' },
  { id: 'value', icon: 'bi-piggy-bank' },
] as const
</script>

<template>
  <section id="benefits" class="sofax surface-alt overflow-hidden">
    <div class="section-space">
      <div class="shell">
        <div class="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <!--
            The template's content image (a picture over an offset shape), with
            a clean, watermark-free CV sheet as the picture and two floating
            cards naming what Premium unlocks. The template ships only grey
            placeholders there, so the composition is drawn rather than a photo.
          -->
          <div class="relative mx-auto w-full max-w-[31.25rem] pb-[3.125rem] pr-[3.125rem]">
            <div
              class="absolute inset-0 left-[3.125rem] top-[3.125rem] rounded-[0.625rem] bg-lilac/40 dark:bg-lilac/15"
              aria-hidden="true"
            />

            <div class="relative overflow-hidden rounded-[0.625rem] border border-line bg-white shadow-lift dark:border-gray-700">
              <NuxtImg
                src="/executive.png"
                :alt="t('homepage.subscriptionBenefits.premium.features.pdfExport')"
                width="458"
                height="558"
                fit="cover"
                :modifiers="{ position: 'top' }"
                format="webp"
                quality="85"
                loading="lazy"
                sizes="xs:340px md:458px"
                class="aspect-[458/558] w-full object-cover object-top"
              />
            </div>

            <div
              class="absolute right-0 top-[38%] inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-accent-strong px-3 py-1.5 !text-xs font-semibold !leading-none !text-white shadow-lift md:px-4 md:py-2.5 md:!text-sm"
              aria-hidden="true"
            >
              <i class="bi bi-stars" />
              {{ t('homepage.subscriptionBenefits.premium.features.aiEnhancement') }}
            </div>

            <div
              class="absolute -left-2 bottom-4 flex max-w-[16rem] items-center gap-3 rounded-[0.625rem] bg-white p-3 shadow-lift md:-left-8 md:p-4 dark:bg-gray-800"
              aria-hidden="true"
            >
              <SofaxIconTile icon="bi-file-earmark-check" round size="sm" />
              <div>
                <div class="display-heading display-heading-6 !leading-tight">
                  {{ t('homepage.subscriptionBenefits.premium.features.pdfExport') }}
                </div>
                <div class="mt-0.5 text-sm text-muted dark:text-gray-400">
                  {{ t('homepage.subscriptionBenefits.premium.features.allTemplates') }}
                </div>
              </div>
            </div>

            <img
              src="/sofax/content-shape-1.svg"
              alt=""
              aria-hidden="true"
              width="125"
              height="137"
              class="pointer-events-none absolute -left-10 -top-10 -z-10 hidden xl:inline-block"
            >
          </div>

          <div>
            <div class="eyebrow mb-5">{{ t('homepage.subscriptionBenefits.premium.badge') }}</div>
            <h2 class="mb-5">{{ t('homepage.subscriptionBenefits.title') }}</h2>
            <p class="mb-10 text-lg">{{ t('homepage.subscriptionBenefits.subtitle') }}</p>

            <ul class="mb-10 grid grid-cols-1 gap-6 sm:grid-cols-2">
              <li v-for="item in highlights" :key="item.id" class="flex gap-4">
                <SofaxIconTile :icon="item.icon" round size="sm" />
                <div>
                  <div class="display-heading display-heading-5 mb-1">
                    {{ t(`homepage.subscriptionBenefits.highlights.${item.id}.title`) }}
                  </div>
                  <p class="text-base">
                    {{ t(`homepage.subscriptionBenefits.highlights.${item.id}.description`) }}
                  </p>
                </div>
              </li>
            </ul>

            <div class="flex flex-wrap items-center gap-x-8 gap-y-4">
              <SofaxBtn
                :to="isAuthenticated ? '/admin/premium' : '/registration'"
                :label="t('homepage.subscriptionBenefits.premium.cta')"
                variant="accent"
              />
              <div class="text-base">
                <span class="block font-title font-bold !text-ink dark:!text-white">
                  {{ jobPackPrice }} · {{ t('homepage.subscriptionBenefits.premium.period') }}
                </span>
                <span class="block !text-sm !text-muted dark:!text-gray-400">
                  {{ t('homepage.subscriptionBenefits.premium.yearlyHint', { price: yearlyPrice }) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
